import React, { useState } from 'react';
import { Sparkles, ShieldCheck, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';

const ProTipsEditor = ({ product, updateProduct }) => {
  const initial = Array.isArray(product.pro_tips) && product.pro_tips.length === 3
    ? product.pro_tips
    : ['', '', ''];
  const [tips, setTips] = useState(initial);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const dirty = tips.some((t, i) => t !== (product.pro_tips?.[i] || ''));

  const setTip = (i, val) => setTips(prev => prev.map((t, j) => (j === i ? val : t)));

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/admin/products/${product.id}/pro-tips/generate`);
      const next = r.data?.pro_tips;
      if (Array.isArray(next) && next.length === 3) {
        setTips(next);
        // Also propagate to parent so the product card reflects the change immediately.
        updateProduct(product.id, { pro_tips: next });
        toast.success('Pro tips generated');
      } else {
        toast.error('Unexpected response');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const save = async () => {
    setSaving(true);
    const cleaned = tips.map(t => (t || '').trim()).filter(Boolean);
    if (cleaned.length !== 3) {
      toast.error('Provide all 3 tips or click Generate');
      setSaving(false); return;
    }
    try {
      await updateProduct(product.id, { pro_tips: cleaned });
      toast.success('Pro tips saved');
    } catch (e) {
      toast.error('Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="border-t border-neutral-200 pt-2 mt-2" data-testid={`pro-tips-editor-${product.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] uppercase text-[#F46A25] font-bold flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Professional usage tips (EEAT)
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid={`pro-tips-generate-${product.id}`}
          className="text-[10px] font-bold border border-neutral-300 hover:border-[#F46A25] hover:text-[#F46A25] px-2 py-1 rounded-sm flex items-center gap-1 disabled:opacity-60"
          title="Ask the LLM to write 3 expert tips for this product"
        >
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {generating ? 'Generating…' : 'Auto-generate'}
        </button>
      </div>
      <div className="space-y-1">
        {tips.map((t, i) => (
          <textarea
            key={i}
            rows={2}
            value={t}
            onChange={e => setTip(i, e.target.value)}
            placeholder={`Tip ${i + 1} — 15-25 words, expert insight`}
            data-testid={`pro-tip-input-${product.id}-${i}`}
            className="w-full border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
          />
        ))}
      </div>
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          data-testid={`pro-tips-save-${product.id}`}
          className="mt-2 bg-[#F46A25] hover:bg-[#e05a15] text-white text-[11px] font-bold px-3 py-1 rounded-sm flex items-center gap-1 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save tips
        </button>
      )}
    </div>
  );
};

export default ProTipsEditor;
